import { BridgeToken, bridgeTokens } from '../constants/bridgeTokens';
import { Address, formatUnits, zeroAddress } from 'viem';
import { lzDefaultAdapterParams } from '../constants/lazyerZeroChains';

export function isBigIntGreaterThanZero(value: bigint): boolean {
  return parseInt(value.toString()) > 0;
}

export function firstDecimals(num: number, decimals: number): number {
  // Convert the number to a string
  const numStr = num.toString();

  // Find the position of the decimal point
  const decimalIndex = numStr.indexOf('.');

  if (decimalIndex !== -1) {
    // If the number is a decimal, proceed to truncate it
    // Calculate the index up to which we want to keep the string
    // We add 1 to account for the decimal point itself, and 6 for the decimal places
    const endIndex = decimalIndex + 1 + decimals;

    // Use substring to get the string up to the desired number of decimal places
    const truncatedStr = numStr.substring(0, Math.min(endIndex, numStr.length));

    // Convert the truncated string back to a number and return it
    return parseFloat(truncatedStr);
  } else {
    // If there's no decimal part, return the number as is
    return num;
  }
}

export function formatBridgeTokenAmount(
  amount: bigint,
  tokenDecimals: number,
  displayDecimals: number
): string {
  const [whole, fraction = ''] = formatUnits(amount, tokenDecimals).split('.');
  const truncatedFraction = fraction
    .slice(0, displayDecimals)
    .replace(/0+$/, '');

  return truncatedFraction ? `${whole}.${truncatedFraction}` : whole;
}

export function formatBridgeNativeFeeAmount(
  amount: bigint,
  displayDecimals: number
): string {
  const formattedAmount = formatBridgeTokenAmount(amount, 18, displayDecimals);

  if (amount > BigInt(0) && formattedAmount === '0') {
    return `<0.${'0'.repeat(displayDecimals - 1)}1`;
  }

  return formattedAmount;
}

export function formatBridgeBalanceAmount(
  amount: bigint,
  tokenDecimals: number,
  displayDecimals: number
): string {
  const [whole, fraction = ''] = formatUnits(amount, tokenDecimals).split('.');
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (displayDecimals === 0) {
    return groupedWhole;
  }

  return `${groupedWhole}.${fraction
    .slice(0, displayDecimals)
    .padEnd(displayDecimals, '0')}`;
}

export function getMaximumBridgeAmount(
  tokenBalance: bigint,
  destinationProxyLiquidity?: bigint
): bigint {
  if (destinationProxyLiquidity === undefined) {
    return tokenBalance;
  }

  return tokenBalance < destinationProxyLiquidity
    ? tokenBalance
    : destinationProxyLiquidity;
}

export function hasSufficientBridgeAllowance(
  allowance: bigint,
  amount: bigint
): boolean {
  return amount > BigInt(0) && allowance >= amount;
}

export function getBridgeSendCallParams(refundAddress: Address) {
  return {
    refundAddress,
    zroPaymentAddress: zeroAddress,
    adapterParams: lzDefaultAdapterParams,
  } as const;
}

export type BridgeNativeFeeQuote = {
  key: string;
  value: bigint;
};

export function getEffectiveBridgeNativeFee(
  estimatedFee: bigint,
  quoteKey: string,
  cachedQuote?: BridgeNativeFeeQuote
): bigint {
  if (estimatedFee > BigInt(0)) {
    return estimatedFee;
  }

  return cachedQuote?.key === quoteKey ? cachedQuote.value : BigInt(0);
}

export const getSupportedTokens = (chainId: number) => {
  const supportedTokens = bridgeTokens.filter((token: BridgeToken) => {
    return token.address[chainId] !== undefined && token.enabled;
  });
  return supportedTokens;
};
